import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { Select } from '@/components/ui/Select';
import {
  CLIENT_TYPES,
  PAYMENT_METHODS,
  PURCHASE_HORIZONS,
  TEMPERATURES,
  type Lead,
} from '@/types/app';
import { useUpdateLead } from './useLeads';

/**
 * Editing a lead.
 *
 * Only the qualification fields a rep owns are here. Stage, owner, SLA, the counters
 * and the capture attribution are all absent — not hidden, absent: their UPDATE
 * privilege is revoked at column level, so putting them in this form would produce
 * a field that silently fails to save. Stage changes go through the stage control on
 * the detail page; ownership through transfer (#5).
 *
 * phone_primary is deliberately not editable. It is the duplicate-detection key
 * (#3), so changing it would re-identify the record; correcting a wrong number is a
 * deliberate action, not a quiet edit.
 */
export function EditLeadDialog({
  open,
  lead,
  onClose,
}: {
  open: boolean;
  lead: Lead;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const update = useUpdateLead();

  const [form, setForm] = useState({
    full_name: lead.full_name,
    email: lead.email ?? '',
    whatsapp_phone: lead.whatsapp_phone ?? '',
    client_type: lead.client_type ?? '',
    temperature: lead.temperature ?? '',
    budget_min: lead.budget_min ?? '',
    budget_max: lead.budget_max ?? '',
    bedrooms: lead.bedrooms?.toString() ?? '',
    payment_method: lead.payment_method ?? '',
    purchase_horizon: lead.purchase_horizon ?? '',
    preferred_areas: lead.preferred_areas.join('، '),
    next_action_at: lead.next_action_at ? lead.next_action_at.slice(0, 16) : '',
    next_action_note: lead.next_action_note ?? '',
  });
  const [error, setError] = useState<string | null>(null);

  const set = (patch: Partial<typeof form>) => setForm((f) => ({ ...f, ...patch }));

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);

    try {
      await update.mutateAsync({
        id: lead.id,
        patch: {
          full_name: form.full_name.trim(),
          email: form.email.trim() || null,
          whatsapp_phone: form.whatsapp_phone.trim() || null,
          client_type: (form.client_type || null) as Lead['client_type'],
          temperature: (form.temperature || null) as Lead['temperature'],
          budget_min: form.budget_min || null,
          budget_max: form.budget_max || null,
          bedrooms: form.bedrooms ? Number(form.bedrooms) : null,
          payment_method: (form.payment_method || null) as Lead['payment_method'],
          purchase_horizon: (form.purchase_horizon || null) as Lead['purchase_horizon'],
          preferred_areas: form.preferred_areas
            .split(/[،,]/)
            .map((a) => a.trim())
            .filter(Boolean),
          next_action_at: form.next_action_at ? new Date(form.next_action_at).toISOString() : null,
          next_action_note: form.next_action_note.trim() || null,
        },
      });
      onClose();
    } catch (cause) {
      // The database rejects budget_min > budget_max and a non-E.164 WhatsApp
      // number with readable messages; surfacing them beats inventing our own.
      setError(cause instanceof Error ? cause.message : t('common.error'));
    }
  };

  return (
    <Modal open={open} title={t('leads.edit')} onClose={onClose}>
      <form onSubmit={(e) => void onSubmit(e)} className="flex flex-col gap-4">
        {error ? <Alert tone="error">{error}</Alert> : null}

        <div className="grid gap-4 sm:grid-cols-2">
          <Input
            label={t('leads.fullName')}
            required
            value={form.full_name}
            onChange={(e) => set({ full_name: e.target.value })}
          />
          <Input
            label={t('leads.phone')}
            dir="ltr"
            value={lead.phone_primary}
            disabled
            hint={t('leads.phoneLocked')}
          />
          <Input
            label={t('leads.whatsapp')}
            dir="ltr"
            value={form.whatsapp_phone}
            onChange={(e) => set({ whatsapp_phone: e.target.value })}
          />
          <Input
            label={t('auth.email')}
            type="email"
            dir="ltr"
            value={form.email}
            onChange={(e) => set({ email: e.target.value })}
          />
          <Select
            label={t('leads.clientType')}
            placeholder="—"
            value={form.client_type}
            onChange={(e) => set({ client_type: e.target.value })}
            options={CLIENT_TYPES.map((c) => ({
              value: c,
              label: t(`clientTypes.${c}` as 'clientTypes.end_user'),
            }))}
          />
          <Select
            label={t('leads.temperature')}
            placeholder="—"
            value={form.temperature}
            onChange={(e) => set({ temperature: e.target.value })}
            options={TEMPERATURES.map((x) => ({
              value: x,
              label: t(`temperatures.${x}` as 'temperatures.hot'),
            }))}
          />
          <Input
            label={t('leads.budgetMin')}
            type="number"
            dir="ltr"
            value={form.budget_min}
            onChange={(e) => set({ budget_min: e.target.value })}
          />
          <Input
            label={t('leads.budgetMax')}
            type="number"
            dir="ltr"
            value={form.budget_max}
            onChange={(e) => set({ budget_max: e.target.value })}
          />
          <Input
            label={t('leads.bedrooms')}
            type="number"
            min={0}
            dir="ltr"
            value={form.bedrooms}
            onChange={(e) => set({ bedrooms: e.target.value })}
          />
          <Select
            label={t('leads.paymentMethod')}
            placeholder="—"
            value={form.payment_method}
            onChange={(e) => set({ payment_method: e.target.value })}
            options={PAYMENT_METHODS.map((p) => ({
              value: p,
              label: t(`paymentMethods.${p}` as 'paymentMethods.cash'),
            }))}
          />
          <Select
            label={t('leads.horizon')}
            placeholder="—"
            value={form.purchase_horizon}
            onChange={(e) => set({ purchase_horizon: e.target.value })}
            options={PURCHASE_HORIZONS.map((h) => ({
              value: h,
              label: t(`horizons.${h}` as 'horizons.immediate'),
            }))}
          />
        </div>

        <Input
          label={t('leads.areas')}
          hint={t('admin.teams.areasHint')}
          value={form.preferred_areas}
          onChange={(e) => set({ preferred_areas: e.target.value })}
        />

        <div className="grid gap-4 sm:grid-cols-2">
          <Input
            label={t('leads.nextActionAt')}
            type="datetime-local"
            dir="ltr"
            hint={t('leads.nextActionHint')}
            value={form.next_action_at}
            onChange={(e) => set({ next_action_at: e.target.value })}
          />
          <Input
            label={t('leads.nextActionNote')}
            value={form.next_action_note}
            onChange={(e) => set({ next_action_note: e.target.value })}
          />
        </div>

        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" loading={update.isPending}>
            {t('common.save')}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
